// src/pages/Home.tsx
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase-config"; // Adjust the import path as needed
import ItemTile from "../components/ItemTile"; // Import your ItemTile component
import { Box, Typography, Grid } from "@mui/material";
import { Variant } from "../store/cartSlice"; // Import Variant type from cartSlice
import { RootState } from "../store/store";
import BigSaleBanner from "../components/BigSaleBanner";

interface Item {
  id: string;
  name: string;
  type: string;
  images?: string[];
  isActive: boolean;
  isSaleActive: boolean;
  variants: Variant[];
}

const Home: React.FC = () => {
  const [items, setItems] = useState<Item[]>([]);
  const userId = useSelector((state: RootState) => state.user.userId);
  const searchQuery = useSelector((state: RootState) => state.search.searchQuery); // Access searchQuery from Redux store
  const [showActiveOnly, setShowActiveOnly] = useState(true); // Add state to toggle active items

  const fetchItems = async (userId: string) => {
    const querySnapshot = await getDocs(collection(db, "items")); // Change "items" to your Firestore collection name

    const itemsArray: Item[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const isSaleActive = data.is_sale_active; // Use the boolean field to check sale status
      const variants = (data.variant || []).map((variant: any) => ({
        id: `${doc.id}-${variant.color}-${variant.material}`,
        color: variant.color,
        colorCode: variant.color_code,
        design: variant.design,
        imageUrl: variant.image_url,
        material: variant.material,
        size: variant.size,
        description: variant.description,
        unitPrice: parseFloat(variant.unit_price),
        salePrice: parseFloat(variant.sale_price),
        availableQuantity: parseInt(variant.quantity, 10),
        quantity: parseInt(variant.quantity, 10), // Add quantity field, initialized to 0
      }));
      
      itemsArray.push({
        id: doc.id,
        name: data.name || "Unnamed Item",
        type: data.type,
        images:data.images,
        isActive: data.is_active,
        isSaleActive: data.is_sale_active,
        variants: variants, // Pass variants here
      });
    });

    // Sort items by the total available quantity of their variants
    const sortedItems = itemsArray.sort((a, b) => {
      const totalQuantityA = a.variants.reduce(
        (sum, variant) => sum + variant.availableQuantity,
        0
      );
      const totalQuantityB = b.variants.reduce(
        (sum, variant) => sum + variant.availableQuantity,
        0
      );
      return totalQuantityB - totalQuantityA; // Descending order
    });

    setItems(sortedItems); // Set sorted items to local state
  };

  useEffect(() => {
    if (userId) {
      fetchItems(userId);
    }
  }, [userId]);

  const filteredItems = items.filter((item) =>
    item.isActive === showActiveOnly &&
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box sx={{ padding: { md: "50px", xs: "20px"} }}>
      <Typography variant="h5" gutterBottom align="center" 
      sx={{ fontSize: { xs: "20px", sm: "30px"} }}>
        Explore our collection of items. Add your favorites to the cart!
      </Typography>
      <Grid container spacing={2} justifyContent="center" paddingTop="20px" alignItems="center"> {/* Use Grid for layout */}
        {filteredItems.map((item) => (
          <Grid item key={item.id} xs={12} sm={6} md={4} display="flex" justifyContent="center"> {/* Adjust grid sizes as needed */}
            <ItemTile item={item} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Home;
